import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FollowButton } from '../components/FollowButton';
import { MessageButton } from '../components/MessageButton';
import BookingModal from './BookingModal';
import ServiceCard from './ServiceCard';
import { API } from '../config/api';
import {
  Headphones, MapPin, Wrench, Star, Share2, Users,
  Verified, Music, Sliders, Settings2, FileText,
  Image, Clock, DollarSign, Award, Loader2
} from 'lucide-react';

export default function EngineerProfileView({ profile, roleProfile, isOwnProfile, followerCount, followingCount, isFollowing, onFollowChange }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('services');
  const [loadingContent, setLoadingContent] = useState(true);
  const [showBooking, setShowBooking] = useState(false);

  const rp = roleProfile || {};
  const specialties = rp.roles || rp.specialties || [];
  const genres = rp.genres || profile.genres || [];
  const equipment = rp.gear || rp.equipment || [];
  const priceRange = rp.price_range || rp.rate_range || profile.rate_range;
  const bio = rp.bio || profile.bio;
  const displayName = rp.display_name || rp.stage_name || profile.name;
  const studioName = rp.studio_name || rp.studio_affiliation;

  useEffect(() => {
    fetchContent();
  }, [profile.user_id]);

  const fetchContent = async () => {
    setLoadingContent(true);
    try {
      const [svcRes, postRes] = await Promise.all([
        fetch(`${API}/services/user/${profile.user_id}`),
        fetch(`${API}/posts?user_id=${profile.user_id}&limit=20`),
      ]);
      if (svcRes.ok) { const d = await svcRes.json(); setServices(d.services || []); }
      if (postRes.ok) { const d = await postRes.json(); setPosts(Array.isArray(d) ? d : d.posts || []); }
    } catch (e) { console.error('Content fetch error:', e); }
    setLoadingContent(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/u/${profile.handle}`;
    try { await navigator.clipboard.writeText(url); toast.success('Profile link copied!'); }
    catch { toast.error('Could not copy link'); }
  };

  const specialtyIcons = { recording: Headphones, mixing: Sliders, mastering: Settings2 };

  return (
    <div className="min-h-screen" data-testid="engineer-profile">
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        {rp.banner_url || profile.banner_url ? (
          <img src={rp.banner_url || profile.banner_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-900/40 via-zinc-900 to-amber-800/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {/* Avatar */}
          <div className="w-28 h-28 rounded-2xl border-4 border-background overflow-hidden bg-muted flex-shrink-0 shadow-xl">
            {(rp.avatar_url || profile.avatar_url || profile.picture) ? (
              <img src={rp.avatar_url || profile.avatar_url || profile.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><Headphones className="w-10 h-10 text-amber-500/50" /></div>
            )}
          </div>

          {/* Info + Actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">{displayName}</h1>
              {(rp.is_verified || profile.is_verified) && <Verified className="w-5 h-5 text-amber-500" />}
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Engineer</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{profile.handle ? `@${profile.handle}` : ""}</p>

            {/* Specialties */}
            {specialties.length > 0 && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {specialties.map(s => {
                  const Icon = specialtyIcons[s.toLowerCase()] || Wrench;
                  return (
                    <Badge key={s} variant="outline" className="bg-amber-500/10 border-amber-500/20 text-amber-300 text-xs">
                      <Icon className="w-3 h-3 mr-1" />{s}
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Location + Rate */}
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {(rp.neighborhood || profile.neighborhood || profile.city || profile.location) && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{rp.neighborhood || profile.neighborhood || profile.city || profile.location}</span>
              )}
              {priceRange && (
                <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{priceRange}</span>
              )}
              {studioName && (
                <span className="flex items-center gap-1"><Settings2 className="w-3 h-3" />{studioName}</span>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-5 mt-3">
              <div className="text-center"><p className="font-bold text-lg">{followerCount}</p><p className="text-xs text-muted-foreground">Followers</p></div>
              <div className="text-center"><p className="font-bold text-lg">{followingCount}</p><p className="text-xs text-muted-foreground">Following</p></div>
              <div className="text-center"><p className="font-bold text-lg">{rp.credits_count || 0}</p><p className="text-xs text-muted-foreground">Credits</p></div>
              <div className="text-center"><p className="font-bold text-lg">{services.length}</p><p className="text-xs text-muted-foreground">Services</p></div>
            </div>

            {/* Action Buttons */}
            {!isOwnProfile && (
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <FollowButton targetUserId={profile.user_id} initialIsFollowing={isFollowing} onFollowChange={onFollowChange} />
                <MessageButton targetUserId={profile.user_id} targetName={displayName} variant="outline" size="default" showText />
                <Button onClick={() => setShowBooking(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="book-engineer-btn">
                  <Headphones className="w-4 h-4 mr-2" />Book Me
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare} data-testid="share-engineer-btn"><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
            {isOwnProfile && (
              <div className="flex items-center gap-2 mt-4">
                <Button variant="outline" onClick={() => navigate('/dashboard/engineer')} data-testid="edit-engineer-profile-btn">
                  <Settings2 className="w-4 h-4 mr-2" />Manage Profile
                </Button>
                <Button variant="ghost" size="icon" onClick={handleShare}><Share2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {bio && <p className="mt-5 text-sm text-muted-foreground max-w-2xl leading-relaxed">{bio}</p>}

        {/* Genres */}
        {genres.length > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Music className="w-4 h-4 text-muted-foreground" />
            {genres.map(g => <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>)}
          </div>
        )}

        {/* Equipment */}
        {equipment.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2"><Wrench className="w-4 h-4" />Equipment</h3>
            <div className="flex flex-wrap gap-2">
              {equipment.map((e, i) => <Badge key={i} variant="outline" className="text-xs bg-muted/20">{e}</Badge>)}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6 overflow-x-auto">
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="eng-tab-services">
              <Wrench className="w-4 h-4" />Services{services.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{services.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="eng-tab-posts">
              <FileText className="w-4 h-4" />Posts{posts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{posts.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex items-center gap-2" data-testid="eng-tab-photos">
              <Image className="w-4 h-4" />Photos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            {loadingContent ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : services.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No services listed</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Add your services from your dashboard.' : 'This engineer hasn\'t listed any services yet.'}</p>
                {isOwnProfile && <Button className="mt-4" onClick={() => navigate('/dashboard/engineer')}>Add Services</Button>}
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {services.map(svc => <ServiceCard key={svc.service_id} service={svc} onBook={!isOwnProfile ? () => setShowBooking(true) : null} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="posts">
            {posts.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No posts yet</h3>
                <p className="text-muted-foreground">{isOwnProfile ? 'Share your work with the community!' : 'No posts from this engineer yet.'}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map(post => (
                  <Card key={post.post_id} className="glass-card border-border p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate(`/post/${post.post_id}`)}>
                    <p className="text-sm">{post.text || post.content}</p>
                    {post.media_url && <img src={post.media_url} alt="" className="mt-3 rounded-lg max-h-64 object-cover" />}
                    <p className="text-xs text-muted-foreground mt-2">{new Date(post.created_at).toLocaleDateString()}</p>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos">
            <Card className="glass-card border-border p-12 text-center">
              <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">No photos yet</h3>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Modal */}
      {showBooking && (
        <BookingModal
          targetId={profile.user_id}
          targetName={displayName}
          targetRole="engineer"
          services={services}
          onClose={() => setShowBooking(false)}
        />
      )}
    </div>
  );
}
